import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { NAV_KEY } from '@/i18n/keys/nav';
import { X } from 'lucide-react';
import { useOnboardingGuide } from '@/contexts/OnboardingGuideContext';
import { useToast } from '@/components/ui/use-toast';

/**
 * Where the chip points:
 * - `right`: beside a sidebar nav link (desktop rail, or the open drawer).
 * - `below`: under an in-page button (Add a task).
 * - `above`: over the phone's bottom-bar More button, while the drawer that
 *   holds the nav link is still closed (#2385).
 */
type Placement = 'right' | 'below' | 'above';
type Point = { x: number; y: number };
/** `below` is where a `right` chip goes instead when it would run past the
 *  viewport's right edge: under the link, so it never covers the link. */
type Anchor = { placement: Placement; x: number; y: number; via: 'target' | 'more'; below?: Point };

/** The chip stays this far inside the viewport's edges. */
const EDGE = 8;

/** True when the element is laid out and at least partly on screen. A closed
 *  drawer is translated off its edge, so its links are present but hidden. */
function onScreen(el: Element | null): el is Element {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return false;
  return r.right > 0 && r.left < window.innerWidth && r.bottom > 0 && r.top < window.innerHeight;
}

export function OnboardingGuideChip() {
  const { guideStep, advanceToRequestBtn, dismissGuide } = useOnboardingGuide();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  // #2109 R3 L1: the nav names are the sidebar's own keys, so the chip says
  // exactly what the reader sees in the sidebar. The circle guide went with
  // the People address book (wave w1023c).
  const { t } = useTranslation(['onboarding', 'nav']);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const chipRef = useRef<HTMLDivElement>(null);
  const [left, setLeft] = useState<number | null>(null);
  const [flipped, setFlipped] = useState(false);

  // The interval below re-measures four times a second; keep the same object
  // when nothing moved so an idle chip does not re-render.
  const place = useCallback((next: Anchor | null) => {
    setAnchor((prev) =>
      prev && next && prev.placement === next.placement && prev.via === next.via && prev.x === next.x && prev.y === next.y
        && prev.below?.x === next.below?.x && prev.below?.y === next.below?.y
        ? prev
        : next);
  }, []);

  const measure = useCallback(() => {
    const isNav = guideStep === 'tasks-nav';
    let target: Element | null = null;
    if (guideStep === 'tasks-nav') target = document.querySelector('a[href="/tasks"]');
    else if (guideStep === 'request-btn') target = document.querySelector('[data-guide="request-btn"]');

    if (onScreen(target)) {
      const r = target.getBoundingClientRect();
      const below = { x: r.left + r.width / 2, y: r.bottom + 10 };
      place(isNav
        ? { placement: 'right', x: r.right + 10, y: r.top + r.height / 2, via: 'target', below }
        : { placement: 'below', ...below, via: 'target' });
      return;
    }
    // #2385: on a phone the nav link sits in the closed drawer. Point at the
    // bottom bar's More button that opens it instead of at nothing.
    const more = isNav ? document.querySelector('[data-guide="more-btn"]') : null;
    if (onScreen(more)) {
      const r = more.getBoundingClientRect();
      place({ placement: 'above', x: r.left + r.width / 2, y: r.top - 10, via: 'more' });
      return;
    }
    place(null);
  }, [guideStep, place]);

  // Re-measure on step change, navigation and resize, and while a guide is
  // active, often enough to follow the drawer sliding open or closed.
  useEffect(() => {
    measure();
    if (!guideStep) return;
    window.addEventListener('resize', measure);
    const timer = window.setInterval(measure, 250);
    return () => {
      window.removeEventListener('resize', measure);
      window.clearInterval(timer);
    };
  }, [measure, guideStep, location.pathname]);

  // Keep the chip inside the viewport. A chip beside a link in the open phone
  // drawer starts near x 250 and would run past a 390 px screen, so it moves
  // under the link. A centred chip (a long label under a button near the
  // edge, or over the corner More button) is clamped to the edges.
  useLayoutEffect(() => {
    if (!anchor) { setFlipped(false); setLeft(null); return; }
    const width = chipRef.current?.getBoundingClientRect().width ?? 0;
    const flip = anchor.placement === 'right' && !!anchor.below && anchor.x + width > window.innerWidth - EDGE;
    setFlipped(flip);
    const centreX = flip && anchor.below ? anchor.below.x : anchor.x;
    if (anchor.placement === 'right' && !flip) { setLeft(null); return; }
    const max = Math.max(EDGE, window.innerWidth - width - EDGE);
    setLeft(Math.min(Math.max(centreX - width / 2, EDGE), max));
  }, [anchor]);

  // Auto-advance: tasks-nav → request-btn when user lands on /tasks
  useEffect(() => {
    if (guideStep === 'tasks-nav' && location.pathname === '/tasks') {
      advanceToRequestBtn();
    }
  }, [location.pathname, guideStep, advanceToRequestBtn]);

  if (!guideStep || !anchor) return null;
  const view: Anchor = flipped && anchor.below
    ? { ...anchor, placement: 'below', x: anchor.below.x, y: anchor.below.y }
    : anchor;

  async function handleDismiss() {
    await dismissGuide();
    toast({ title: t('guide.skippedTitle'), description: t('guide.tasksSkipped', { nav: t(NAV_KEY.tasks) }) });
    navigate('/dashboard');
  }

  const centred = view.placement !== 'right';
  const chipLeft = centred ? (left ?? view.x) : view.x;
  const transform =
    view.placement === 'right' ? 'translateY(-50%)'
    : view.placement === 'above' ? 'translateY(-100%)'
    : undefined;
  // The arrow points at the anchor's centre, wherever the clamp put the chip.
  const arrowLeft = centred ? `${view.x - chipLeft}px` : undefined;
  const navName = t(NAV_KEY.tasks);

  return (
    <div
      ref={chipRef}
      data-placement={view.placement}
      // z-[70]: above the z-[60] drawer and its scrim (the shell stacking
      // contract, #2435), so the chip still shows once the drawer is open.
      className="fixed z-[70] flex w-max max-w-[calc(100vw-1rem)] items-center gap-2 rounded-xl shadow-lg text-body font-medium text-stevens-indigo-solid-fg"
      style={{ background: 'var(--stevens-indigo-solid)', padding: '0.45rem 0.9rem', top: view.y, left: chipLeft, transform }}
      role="status"
      aria-live="polite"
    >
      {/* Left-pointing arrow: the chip is to the right of a nav link */}
      {view.placement === 'right' && (
        <div
          className="absolute -left-2 top-1/2 -translate-y-1/2 w-0 h-0"
          style={{ borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderRight: '8px solid var(--stevens-indigo-solid)' }}
        />
      )}

      {/* Up-pointing arrow: the chip is below a button */}
      {view.placement === 'below' && (
        <div
          className="absolute -top-2 -translate-x-1/2 w-0 h-0"
          style={{ left: arrowLeft, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: '8px solid var(--stevens-indigo-solid)' }}
        />
      )}

      {/* Down-pointing arrow: the chip is above the More button */}
      {view.placement === 'above' && (
        <div
          className="absolute -bottom-2 -translate-x-1/2 w-0 h-0"
          style={{ left: arrowLeft, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '8px solid var(--stevens-indigo-solid)' }}
        />
      )}

      {/* Pulsing dot */}
      <span className="relative flex h-2 w-2 flex-shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
      </span>

      {view.via === 'more' ? (
        <span><Trans t={t} i18nKey="guide.tapMore" values={{ more: t(NAV_KEY.tabMore), nav: navName }} components={{ strong: <strong /> }} /></span>
      ) : guideStep === 'tasks-nav' ? (
        <span><Trans t={t} i18nKey="guide.clickNav" values={{ nav: navName }} components={{ strong: <strong /> }} /></span>
      ) : (
        <span><Trans t={t} i18nKey="guide.clickRequest" components={{ strong: <strong /> }} /></span>
      )}

      <button
        type="button"
        onClick={handleDismiss}
        className="ml-0.5 flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
        aria-label={t('wizard.actions.skip')}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
